import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

/**
 * Shared "⋯" overflow menu.
 *
 * The design rule this exists to serve: every row gets ONE visible action plus a
 * kebab for the rest, so row actions stop competing with the table. Destructive
 * items go last, behind a divider.
 *
 * Accessibility (WCAG 2.1 AA — the audit gate is 0 issues):
 *  - trigger: aria-haspopup="menu" + aria-expanded + aria-label
 *  - menu: role="menu", items role="menuitem"
 *  - ArrowUp/ArrowDown/Home/End move focus, Escape closes, click-outside closes
 *  - focus returns to the trigger on close (so keyboard users aren't dumped at
 *    the top of the document) — INCLUDING when an item is chosen, and BEFORE the
 *    item runs. Choosing an item used to skip this, so the item unmounted holding
 *    focus and focus fell to <body>; a dialog the item opened (the Queue's Dismiss
 *    confirm) then remembered <body> as where to return, and closing it threw a
 *    keyboard user to the top of the page (WCAG 2.4.3, measured on QA 2026-10-14).
 *    Focusing the trigger first means whatever the item opens records the trigger.
 *
 * `items`: [{ key, label, icon?, onSelect, danger?, disabled?, dividerBefore? }]
 *
 * ## Why this renders in a PORTAL, not in place
 *
 * The menu used to be an `absolute` child of the trigger, which meant ANY
 * ancestor with `overflow: hidden` clipped it. Two shipped surfaces do exactly
 * that — the shared `DataGrid` card wrapper (`DataGrid.jsx:551`) and the
 * schedules list wrapper — so on the last row of a grid the lower menu items
 * were cut off by the container edge and unreachable. A portal to `document.body`
 * plus `position: fixed` cannot be clipped by an ancestor, by construction.
 *
 * `z-[60]` rather than `z-50`: `Modal` and `ReportJobDrawer` are both `z-50`, and
 * relying on portal-append order to win the tie is the kind of thing that breaks
 * silently when someone reorders a tree.
 *
 * ## Placement
 *
 * `direction`: 'auto' (default) | 'down' | 'up'. 'auto' measures the real menu
 * height against the viewport and flips up when there isn't room below — which is
 * what makes the last row of a grid work without every caller having to know it
 * is the last row. 'down'/'up' remain explicit overrides for callers that know
 * better (the job drawer's footer pins 'up'). The resolved placement is exposed
 * as `data-placement` so tests can assert it without depending on class names.
 *
 * Known related trap: a ticking value reaching a `columns` memo remounts the row
 * and takes this menu's `open` state with it (see `useTicker`). Portalling does
 * not change that — keep clocks out of `columns`.
 */

/** Gap between trigger and menu, and the minimum inset from the viewport edge. */
const GAP = 4
/** Used only for the first paint, before the real menu height can be measured. */
const ESTIMATED_HEIGHT = 44
const ESTIMATED_WIDTH = 196

const KebabMenu = ({ items = [], label = 'More actions', align = 'right', direction = 'auto', buttonClassName = '' }) => {
  const [open, setOpen] = useState(false)
  const [coords, setCoords] = useState(null)
  const rootRef = useRef(null)
  const menuRef = useRef(null)
  const triggerRef = useRef(null)
  const itemRefs = useRef([])
  const menuId = useId()
  const triggerId = useId()

  const enabled = items.filter((i) => !i.disabled)

  // Coords are deliberately NOT cleared on close: `attachMenu` recomputes them
  // before paint on every open, so a stale value can never be shown.
  //
  // A plain function, not a useCallback: it is only ever called from a JSX handler,
  // and wrapping a setState-only closure defeats the React Compiler's memoization
  // inference for the whole component.
  const close = () => {
    setOpen(false)
    triggerRef.current?.focus()
  }

  /**
   * Place the menu against the trigger's viewport rect.
   *
   * Called from the menu's own ref callback (i.e. after it is in the DOM, so the
   * height measured is the REAL rendered height — an estimate flips the wrong way
   * for a six-item menu) and from the scroll/resize listeners. Deliberately NOT
   * from an effect body: that is a cascading-render hazard and the repo's lint
   * rules reject it.
   */
  const place = useCallback(() => {
    const trigger = triggerRef.current
    if (!trigger) return
    const r = trigger.getBoundingClientRect()
    const h = menuRef.current?.offsetHeight || ESTIMATED_HEIGHT
    const w = menuRef.current?.offsetWidth || ESTIMATED_WIDTH
    const vh = window.innerHeight
    const vw = window.innerWidth

    const roomBelow = vh - r.bottom
    const roomAbove = r.top
    let up
    if (direction === 'up') up = true
    else if (direction === 'down') up = false
    else up = roomBelow < h + GAP && roomAbove > roomBelow

    // Clamp into the viewport either way: flipping up is no help if the menu is
    // taller than the space above it too.
    const top = Math.max(GAP, Math.min(up ? r.top - h - GAP : r.bottom + GAP, vh - h - GAP))
    const left = Math.max(GAP, Math.min(align === 'right' ? r.right - w : r.left, vw - w - GAP))
    setCoords({ top, left, placement: up ? 'up' : 'down' })
  }, [align, direction])

  /**
   * Attaching the menu measures and places it. A ref callback runs after the node
   * is in the DOM and before paint, which is what a layout effect would have given
   * us without the setState-in-effect problem.
   */
  const attachMenu = useCallback((el) => {
    menuRef.current = el
    if (el) place()
  }, [place])

  // Reposition rather than close: the menu can sit inside a scrolling grid, and a
  // menu that vanishes when the user nudges the wheel reads as a broken click.
  // Capture phase so scrolls on inner containers are seen too. The effect body only
  // subscribes; the setState happens in the listener, which is the sanctioned shape.
  useEffect(() => {
    if (!open) return undefined
    const onMove = () => place()
    window.addEventListener('scroll', onMove, true)
    window.addEventListener('resize', onMove)
    return () => {
      window.removeEventListener('scroll', onMove, true)
      window.removeEventListener('resize', onMove)
    }
  }, [open, place])

  // Click-outside + Escape. Bound only while open so we don't add a document
  // listener per row on a 25-row grid. The menu lives in a portal, so it is NOT
  // inside rootRef — it must be excluded explicitly, or mousedown on an item
  // unmounts the menu before the click can land and onSelect never fires.
  useEffect(() => {
    if (!open) return undefined
    const onDoc = (e) => {
      if (rootRef.current?.contains(e.target)) return
      if (menuRef.current?.contains(e.target)) return
      setOpen(false)
    }
    // Inlined rather than calling `close` so this subscription does not depend on
    // an unmemoized function and re-bind on every render.
    const onKey = (e) => {
      if (e.key !== 'Escape') return
      e.stopPropagation()
      setOpen(false)
      triggerRef.current?.focus()
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey) }
  }, [open])

  // Focus the first item when the menu opens.
  useEffect(() => {
    if (open) itemRefs.current[0]?.focus()
  }, [open])

  const focusAt = (idx) => {
    const list = itemRefs.current.filter(Boolean)
    if (!list.length) return
    const next = (idx + list.length) % list.length
    list[next]?.focus()
  }

  const onMenuKeyDown = (e, idx) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); focusAt(idx + 1) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); focusAt(idx - 1) }
    else if (e.key === 'Home') { e.preventDefault(); focusAt(0) }
    else if (e.key === 'End') { e.preventDefault(); focusAt(enabled.length - 1) }
  }

  const onTriggerKeyDown = (e) => {
    // Opening with an arrow key is the expected menu-button behaviour.
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); setOpen(true) }
  }

  if (!items.length) return null

  const menu = (
    <div
      ref={attachMenu}
      id={menuId}
      role="menu"
      // Spec 216: named BY its trigger, not by a copy of the label. The menu is portaled to
      // <body>, outside whatever keeps the row out of Pendo, and a label that names a firm's
      // report ("More actions for <name>") must not travel there. Same accessible name.
      aria-labelledby={triggerId}
      data-placement={coords?.placement || 'down'}
      style={{ position: 'fixed', top: coords?.top ?? 0, left: coords?.left ?? 0 }}
      className="z-[60] min-w-[196px] rounded-xl border border-gray-200 bg-white p-1 shadow-lg"
    >
      {items.map((item) => {
        if (item.disabled) {
          return (
            <div key={item.key} className="flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm text-gray-300" aria-disabled="true">
              {item.icon}
              {item.label}
            </div>
          )
        }
        const idx = enabled.indexOf(item)
        return (
          <div key={item.key}>
            {item.dividerBefore && <hr className="my-1 border-gray-100" />}
            <button
              ref={(el) => { itemRefs.current[idx] = el }}
              type="button"
              role="menuitem"
              onKeyDown={(e) => onMenuKeyDown(e, idx)}
              onClick={(e) => { e.stopPropagation(); close(); item.onSelect?.() }}
              className={`flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm transition-colors ${
                item.danger ? 'text-red-600 hover:bg-red-50' : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              {item.icon}
              {item.label}
            </button>
          </div>
        )
      })}
    </div>
  )

  return (
    <div className="relative" ref={rootRef}>
      <button
        ref={triggerRef}
        id={triggerId}
        type="button"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o) }}
        onKeyDown={onTriggerKeyDown}
        className={`inline-flex h-7 w-7 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 ${open ? 'bg-gray-100 text-gray-700' : ''} ${buttonClassName}`}
      >
        <span aria-hidden="true" className="text-[15px] leading-none tracking-widest">⋯</span>
      </button>

      {open && createPortal(menu, document.body)}
    </div>
  )
}

export default KebabMenu
